import { REEBELO_STORE_TO_LOCALE } from '@lambda/reebelo';
import { DefaultSeo } from 'next-seo';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { serialiseJsonLd } from '@/components/v2/product/productSchema';
import settings from '@/settings';
import { SITE_NAME } from '@/lib/site-name';

const STORE_CONFIG: Partial<Record<typeof settings.store, { url: string }>> = {
  'reebelo-us': { url: 'https://reebelo.com' },
  'reebelo-ca': { url: 'https://reebelo.ca' },
  'reebelo-uk': { url: 'https://reebelo.co.uk' },
  'reebelo-au': { url: 'https://reebelo.com.au' },
  'reebelo-nz': { url: 'https://reebelo.co.nz' },
  quista: { url: 'https://reebelo.sg' },
  'reebelo-fr': { url: 'https://reebelo.fr' },
  'reebelo-de': { url: 'https://reebelo.de' },
  'reebelo-es': { url: 'https://reebelo.es' },
  'reebelo-it': { url: 'https://reebelo.it' },
  'reebelo-se': { url: 'https://reebelo.se' },
  'reebelo-ie': { url: 'https://reebelo.ie' },
  'reebelo-nl': { url: 'https://reebelo.nl' },
  'reebelo-at': { url: 'https://reebelo.at' },
  'reebelo-be': { url: 'https://reebelo.be' },
  'reebelo-pt': { url: 'https://reebelo.pt' },
  'reebelo-fi': { url: 'https://reebelo.fi' },
};

const storeConfig = STORE_CONFIG[settings.store];
const locale = storeConfig ? REEBELO_STORE_TO_LOCALE[settings.store] : null;

const SHARE_IMAGE = 'https://edge.reebelo.com/images/opengraph.png';

export function WebsiteSchema({
  title,
  description,
}: {
  title: string;
  description: string;
}) {
  const router = useRouter();

  if (!storeConfig || !locale) {
    return null;
  }

  // `asPath` already carries its leading slash, so the extra one here gave
  // every page an `og:url` of the form `https://reebelo.co.uk//`. Google only
  // honours a homepage's `og:site_name` when its `og:url` resolves to the
  // domain root, and a doubled slash does not.
  const path = router.asPath.split('?')[0] ?? '/';
  const isHomepage = path === '/';

  return (
    <>
      <DefaultSeo
        norobots
        // 🔹 Basic Page Info
        title={title}
        description={description}
        // 🔹 Open Graph
        openGraph={{
          type: 'website',
          locale,
          url: `${storeConfig.url}${path}`,
          siteName: SITE_NAME,
          title,
          description,
          images: [
            {
              url: SHARE_IMAGE,
              width: 1200,
              height: 630,
              alt: SITE_NAME,
              type: 'image/jpeg',
            },
          ],
        }}
        // 🔹 Twitter Card — X reads its own tags first and only falls back to
        // Open Graph, so a card without them can render untitled or imageless.
        twitter={{ cardType: 'summary_large_image' }}
        // 🔹 Additional Tags
        additionalMetaTags={[
          { name: 'publisher', content: 'Reebelo' },
          { name: 'twitter:title', content: title },
          { name: 'twitter:description', content: description },
          { name: 'twitter:image', content: SHARE_IMAGE },
          { name: 'twitter:image:alt', content: SITE_NAME },
        ]}
      />
      {/* WebSite JSON-LD is the only signal Google treats as authoritative for
      the name it shows beside our results; without it the name is guessed from
      heuristics, which is why some of our domains render as "Reebelo US" and
      others as the bare domain. Google reads it from the homepage only. */}
      {isHomepage && (
        <Head>
          <script
            key="jsonld-WebSite"
            type="application/ld+json"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: serialiseJsonLd({
                '@context': 'https://schema.org',
                '@type': 'WebSite',
                name: SITE_NAME,
                alternateName: 'Reebelo',
                url: `${storeConfig.url}/`,
                // The store's own BCP-47 tag, so the entity is not assumed to
                // be the same language across every domain.
                inLanguage: locale,
              }),
            }}
          />
        </Head>
      )}
    </>
  );
}
